import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Typography,
  Stack,
  Banner,
  Button,
  SubmitButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import {
  useStartHudVoucherDraftAutomationMutation,
  useUpdateHudVoucherBeginDateMutation,
} from '../hooks';
import useIntl from '../../../hooks/useIntl';
import messages from './messages';

// With create-past-vouchers on, the begin month may be up to this many
// months before the next month. Mirrors PAST_VOUCHER_MONTHS_ALLOWED in fortress-backend.
const PAST_VOUCHER_MONTHS_ALLOWED = 4;

const MODES = {
  SETUP: 'setup',
  MANAGE: 'manage',
};

export const getVoucherMinDate = (createPastVouchers) =>
  createPastVouchers
    ? moment()
        .add(1, 'month')
        .subtract(PAST_VOUCHER_MONTHS_ALLOWED, 'months')
        .startOf('month')
    : moment().add(1, 'month').startOf('month');

const VoucherSetupModal = ({
  open,
  onClose,
  initialBeginDate,
  organizationId,
  propertyId,
  mode = MODES.SETUP,
}) => {
  const intl = useIntl();
  const [beginDate, setBeginDate] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const { createPastVouchers } = useFlags();
  // Computed every render so the floor stays fresh when the page spans a month boundary.
  const minDate = getVoucherMinDate(createPastVouchers);

  const isManageMode = mode === MODES.MANAGE;

  const handleClose = useCallback(() => {
    setBeginDate(null);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleMutationError = useCallback((error) => {
    const errorMessage =
      (typeof error === 'string' && error) ||
      error?.message ||
      error?.error ||
      'An error occurred. Please try again.';
    setSubmitError(errorMessage);
  }, []);

  // Setup mode mutation
  const setupMutation = useStartHudVoucherDraftAutomationMutation({
    organizationId,
    propertyId,
    onSuccess: handleClose,
    onError: handleMutationError,
  });

  // Manage mode mutations
  const updateMutation = useUpdateHudVoucherBeginDateMutation({
    organizationId,
    propertyId,
    onSuccess: handleClose,
    onError: handleMutationError,
  });

  const isSubmitting = isManageMode
    ? updateMutation.isLoading
    : setupMutation.isLoading;

  const dateError = useMemo(() => {
    if (!beginDate) return null;
    const selectedDate = moment(beginDate).startOf('month');
    if (selectedDate.isBefore(minDate, 'month')) {
      return 'pastDate';
    }
    return null;
  }, [beginDate, minDate]);

  const dateErrorMessage = useMemo(() => {
    if (!dateError) return undefined;
    if (createPastVouchers) {
      return intl.formatMessage(messages.dateErrorPastWindow, {
        minMonth: minDate.format('MM/YYYY'),
      });
    }
    return intl.formatMessage(messages.dateError);
  }, [dateError, createPastVouchers, intl, minDate]);

  useEffect(() => {
    if (open) {
      if (isManageMode && initialBeginDate) {
        setBeginDate(moment(initialBeginDate).startOf('month'));
      } else {
        setBeginDate(null);
      }
      setSubmitError(null);
    }
  }, [open, initialBeginDate, isManageMode]);

  const handleDateChange = (date) => {
    setBeginDate(date ? moment(date).startOf('month') : null);
  };

  const handleSubmit = () => {
    if (
      !beginDate ||
      dateError ||
      isSubmitting ||
      !organizationId ||
      !propertyId
    )
      return;

    setSubmitError(null);

    if (isManageMode) {
      const beginDateIso = moment(beginDate).startOf('month').toISOString();
      updateMutation.mutate({ beginDate: beginDateIso });
    } else {
      const beginDateIso = formatDateDB(moment(beginDate).startOf('month'));
      setupMutation.mutate({ beginDate: beginDateIso });
    }
  };

  const actions = (
    <>
      <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
        <FormattedMessage {...messages.cancel} />
      </Button>
      <SubmitButton
        variant="primary"
        onClick={handleSubmit}
        isSubmitting={isSubmitting}
        disabled={
          !beginDate ||
          dateError ||
          (isManageMode && (!organizationId || !propertyId))
        }
      >
        <FormattedMessage {...messages.confirm} />
      </SubmitButton>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      title={
        <FormattedMessage
          {...(isManageMode ? messages.manageTitle : messages.setupTitle)}
        />
      }
      actions={actions}
      ModalProps={{
        PaperProps: {
          sx: {
            maxWidth: '527px',
          },
        },
      }}
    >
      <Stack spacing={2.5}>
        {submitError && <Banner status="error" message={submitError} />}

        {isManageMode ? (
          <Typography variant="bodyMedium">
            <FormattedMessage
              {...messages.updateInstruction}
              values={{
                voucherMonthYear: (
                  <Typography
                    component="span"
                    variant="bodyMedium"
                    fontWeight="bold"
                  >
                    <FormattedMessage {...messages.voucherMonthAndYear} />
                  </Typography>
                ),
              }}
            />
          </Typography>
        ) : (
          <>
            <Typography variant="bodyMedium">
              <FormattedMessage {...messages.readyQuestion} />
            </Typography>
            <Typography variant="bodyMedium">
              <FormattedMessage
                {...messages.beginDateQuestion}
                values={{
                  voucherMonthYear: (
                    <Typography
                      component="span"
                      variant="bodyMedium"
                      fontWeight="bold"
                    >
                      <FormattedMessage {...messages.voucherMonthAndYear} />
                    </Typography>
                  ),
                  firstMonthsVoucher: (
                    <Typography
                      component="span"
                      variant="bodyMedium"
                      fontWeight="bold"
                    >
                      <FormattedMessage {...messages.firstMonthsVoucher} />
                    </Typography>
                  ),
                }}
              />
            </Typography>
          </>
        )}

        <DatePicker
          label={intl.formatMessage(messages.beginDateLabel)}
          name="beginDate"
          views={['month', 'year']}
          format="MM/YYYY"
          value={beginDate ? moment(beginDate) : null}
          onAccept={handleDateChange}
          onChange={handleDateChange}
          handleClear={() => setBeginDate(null)}
          placeholder="MM/YYYY"
          minDate={minDate}
          disabled={isSubmitting}
          error={!!dateError}
          helperText={dateErrorMessage}
        />

        <Banner
          status="warning"
          message={
            <FormattedMessage
              {...messages.avdWarning}
              values={{
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          }
        />

        <Banner
          status="info"
          message={
            <FormattedMessage
              {...(isManageMode
                ? messages.updateConfirmation
                : messages.infoMessage)}
            />
          }
        />
      </Stack>
    </Modal>
  );
};

VoucherSetupModal.MODES = MODES;

VoucherSetupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialBeginDate: PropTypes.string,
  organizationId: PropTypes.string,
  propertyId: PropTypes.string,
  mode: PropTypes.oneOf([MODES.SETUP, MODES.MANAGE]),
};

export default VoucherSetupModal;
